import { useEffect, useState, useMemo, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  LayoutGrid, Table as TableIcon, Building2, Star, RefreshCw, Search, ChevronLeft, ChevronRight, StickyNote,
} from "lucide-react";
import { ORDERED_STATUSES, STATUS_CARD_STYLE, statusOf, styleOf } from "./statusMeta";
import RoomActionDrawer, { STATUS_DOT } from "./RoomActionDrawer";
import { useStayCheckedOut } from "@/hooks/useStayCheckedOut";
import RoomTimeline from "@/components/RoomTimeline";
import { CalendarRange } from "lucide-react";

const MODE_BTN = "h-9 px-4 rounded-md text-[13px] font-semibold border transition-colors inline-flex items-center gap-1.5";

const lookupCache = {};
async function loadLookup(key) {
  if (lookupCache[key]) return lookupCache[key];
  try {
    const { data } = await api.get(`/master/${key}/?page_size=500`);
    lookupCache[key] = data.items;
    return data.items;
  } catch { return []; }
}

function KpiCard({ label, value, active, onClick, tone = "neutral" }) {
  // Bold, high-contrast KPI: strong border on active, dark navy number, uppercase muted label.
  const tones = {
    neutral: "border-hocp-border hover:border-hocp-borderStrong",
    success: "border-emerald-300 hover:border-emerald-400",
    warning: "border-amber-300 hover:border-amber-400",
    info:    "border-sky-300 hover:border-sky-400",
    error:   "border-rose-300 hover:border-rose-400",
    purple:  "border-violet-300 hover:border-violet-400",
  };
  const accents = {
    neutral: "text-hocp-textPrimary",
    success: "text-emerald-800",
    warning: "text-amber-900",
    info:    "text-sky-900",
    error:   "text-rose-800",
    purple:  "text-violet-800",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
      className={`text-left p-4 rounded-lg border-2 bg-white transition-all ${tones[tone]} ${
        active
          ? "ring-2 ring-hocp-orange/50 border-hocp-orange shadow-hocp-md"
          : "shadow-hocp-sm hover:shadow-hocp-md"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-hocp-textTertiary mb-2">{label}</div>
      <div className={`font-display text-[28px] font-bold tracking-tight tabular-nums leading-none ${accents[tone]}`}>{value}</div>
    </button>
  );
}

const STATUS_TONE = {
  total: "neutral", available: "success", occupied: "info", reserved: "warning",
  vacant_clean: "success", vacant_dirty: "warning", out_of_order: "error",
  out_of_service: "error", blocked: "warning", inspection_pending: "warning",
  maintenance: "error", deep_cleaning: "purple",
};
const KPI_ORDER = [
  ["total", "Total Rooms"], ["available", "Available"], ["occupied", "Occupied"], ["reserved", "Reserved"],
  ["vacant_clean", "Vacant Clean"], ["vacant_dirty", "Vacant Dirty"], ["out_of_order", "Out of Order"],
  ["maintenance", "Maintenance"], ["blocked", "Blocked"], ["inspection_pending", "Inspection"],
];

function RoomCard({ snap, onClick, selected = false }) {
  const meta = statusOf(snap.current_status);
  const st = styleOf(snap.current_status);
  const Icon = meta.Icon;
  const blocked = snap.current_status === "blocked";
  const hasNote = !!snap.last_note_at && (Date.now() - new Date(snap.last_note_at).getTime()) < 30 * 86400000;
  const scheduled = !blocked && !!snap.scheduled_block_from;
  const dot = STATUS_DOT[snap.current_status] || "#97A6A9";
  const fmtShort = (d) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");
  const baseCls = blocked ? "border bg-[#EEF1F4] border-[#B7CACD]" : scheduled ? "border border-dashed bg-[#F4FCFC] border-[#B7CACD]" : `border ${st.bg} ${st.border}`;
  return (
    <button
      onClick={onClick}
      data-testid={`room-card-${snap.room_number}`}
      data-selected={selected ? "true" : "false"}
      data-block={blocked ? "active" : scheduled ? "scheduled" : "none"}
      className={`relative text-left rounded-lg p-3 cursor-pointer transition-[border-color,box-shadow,background-color,transform] duration-150 hover:border-[#37F4B5] hover:shadow-[0_0_0_2px_rgba(55,244,181,0.2)] ${selected ? "border-2 border-[#37F4B5] bg-[#DBFCEF]" : baseCls}`}
    >
      <span className={`absolute top-2 right-2 w-2 h-2 rounded-full ${scheduled ? "border border-[#97A6A9] bg-transparent" : ""}`} style={scheduled ? undefined : { background: dot }} data-testid={`room-dot-${snap.room_number}`} aria-hidden />
      {hasNote && (
        <span className="absolute top-1.5 right-6 inline-flex items-center gap-0.5 rounded px-1 py-[1px] bg-[#FEF3C7] text-[#78530E] text-[9.5px] font-bold" title={`${snap.last_note_by || "Note"}: ${snap.last_note_preview || ""}`} data-testid={`room-note-badge-${snap.room_number}`}>
          <StickyNote size={10} strokeWidth={1.75} />{snap.notes_count > 1 ? snap.notes_count : ""}
        </span>
      )}
      <div className="flex items-start justify-between mb-1.5 pr-3">
        <div className="font-display text-[22px] font-bold text-hocp-navy leading-none">{snap.room_number}</div>
        <Icon className={`w-4 h-4 ${st.accent}`} strokeWidth={1.5}/>
      </div>
      {blocked || scheduled ? (
        <div className="mb-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.08em] font-bold rounded px-1.5 py-0.5 bg-[#EEF1F4] border border-[#B7CACD] text-[#3A4A5A]" data-testid={`room-blocked-badge-${snap.room_number}`}>Blocked</span>
          <span className={`text-[10px] ${blocked ? "text-[#3A4A5A]" : "text-[#97A6A9]"}`}>{blocked ? `Until ${fmtShort(snap.blocked_until || snap.scheduled_block_to)}` : `From ${fmtShort(snap.scheduled_block_from)}`}</span>
        </div>
      ) : (
        <div className={`text-[10px] uppercase tracking-[0.12em] font-semibold mb-2 ${st.accent}`}>
          {meta.label}
        </div>
      )}
      {snap.current_guest_name && (
        <div className="text-[12.5px] font-medium text-hocp-textPrimary truncate flex items-center gap-1">
          {snap.is_vip && <Star className="w-3 h-3 text-hocp-gold fill-hocp-gold" />}
          {snap.current_guest_name}
        </div>
      )}
      {snap.expected_checkout_time && (
        <div className="text-[10.5px] text-hocp-textSecondary mono mt-1">
          Out: {(snap.expected_checkout_time || "").slice(0, 10)}
        </div>
      )}
      {snap.next_arrival_date && !snap.current_guest_name && (
        <div className="text-[10.5px] text-hocp-textSecondary mono mt-1">
          Next: {(snap.next_arrival_date || "").slice(0, 10)}
        </div>
      )}
      {snap.housekeeping_status === "dirty" && snap.current_status !== "occupied" && (
        <div className="mt-1.5"><Badge tone="warning">Cleaning required</Badge></div>
      )}
    </button>
  );
}

function FloorView({ snaps, floorsById, onSelect, selectedId }) {
  const byFloor = useMemo(() => {
    const map = {};
    snaps.forEach(s => { (map[s.floor_id || "none"] = map[s.floor_id || "none"] || []).push(s); });
    return map;
  }, [snaps]);
  const orderedFloorIds = Object.keys(byFloor).sort((a, b) => {
    const fa = floorsById[a]?.floor_number ?? 999;
    const fb = floorsById[b]?.floor_number ?? 999;
    return fa - fb;
  });
  return (
    <div className="space-y-5">
      {orderedFloorIds.map((fid) => {
        const f = floorsById[fid];
        const label = f ? `Floor ${f.floor_number} — ${f.floor_name || ""}` : "Unassigned";
        return (
          <div key={fid} data-testid={`floor-section-${fid}`}>
            <div className="text-[12px] mono uppercase tracking-[0.14em] font-semibold text-hocp-textSecondary mb-3 flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5 text-hocp-steel"/>
              <span className="text-hocp-textPrimary">{label}</span>
              <span className="text-hocp-border">·</span>
              <span className="text-hocp-textTertiary normal-case tracking-normal">{byFloor[fid].length} rooms</span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {byFloor[fid].map(s => <RoomCard key={s.id} snap={s} selected={s.room_id === selectedId} onClick={() => onSelect(s)}/>)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TableView({ snaps, onSelect }) {
  return (
    <Card className="overflow-hidden">
      <table className="w-full" data-testid="rooms-table">
        <thead className="bg-hocp-alice/60 border-b-2 border-hocp-border">
          <tr>
            {["Room", "Status", "Housekeeping", "Technical", "Guest", "Departure", "Next Arrival", "VIP"].map(h => (
              <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-[0.12em] font-semibold text-hocp-textSecondary text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {snaps.map(s => {
            const meta = statusOf(s.current_status);
            return (
              <tr
                key={s.id}
                onClick={() => onSelect(s)}
                data-testid={`room-row-${s.room_number}`}
                className="border-b border-hocp-border last:border-0 hover:bg-hocp-alice/40 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 font-mono font-semibold text-hocp-navy">{s.room_number}</td>
                <td className="px-4 py-3"><Badge tone={meta.tone}>{meta.label}</Badge></td>
                <td className="px-4 py-3 text-[12.5px] mono text-hocp-textPrimary">{s.housekeeping_status}</td>
                <td className="px-4 py-3 text-[12.5px] mono text-hocp-textPrimary">{s.technical_status}</td>
                <td className="px-4 py-3 text-[13px] text-hocp-textPrimary flex items-center gap-1">
                  {s.is_vip && <Star className="w-3 h-3 text-hocp-gold fill-hocp-gold"/>}
                  {s.current_guest_name || <span className="text-hocp-textTertiary">—</span>}
                </td>
                <td className="px-4 py-3 text-[12.5px] mono text-hocp-textSecondary">{(s.expected_checkout_time || s.departure_date || "").slice(0, 10) || <span className="text-hocp-textTertiary">—</span>}</td>
                <td className="px-4 py-3 text-[12.5px] mono text-hocp-textSecondary">{(s.next_arrival_date || "").slice(0, 10) || <span className="text-hocp-textTertiary">—</span>}</td>
                <td className="px-4 py-3 text-[12px]">{s.is_vip ? <Badge tone="warning">VIP</Badge> : <span className="text-hocp-textTertiary">—</span>}</td>
              </tr>
            );
          })}
          {!snaps.length && (
            <tr><td colSpan={8} className="px-4 py-10 text-center text-hocp-textTertiary text-sm">No rooms match the filters</td></tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

export default function Inventory() {
  const [kpis, setKpis] = useState({});
  const [snaps, setSnaps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(120);
  const [view, setView] = useState("card");           // card | table | floor
  const [mode, setMode] = useState(() => (typeof window !== "undefined" && window.localStorage.getItem("hocp.inventory.mode")) || "grid"); // grid | timeline
  const switchMode = (m) => { setMode(m); try { window.localStorage.setItem("hocp.inventory.mode", m); } catch { /* ignore */ } };
  const [filters, setFilters] = useState({
    status: "", housekeeping: "", building_id: "", floor_id: "", room_type_id: "", q: "", is_vip: "",
  });
  const [hotels, setHotels] = useState([]); // unused for now, single hotel
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [loading, setLoading] = useState(false);

  const floorsById = useMemo(() => Object.fromEntries(floors.map(f => [f.id, f])), [floors]);

  const loadKpis = useCallback(async () => {
    try {
      const { data } = await api.get("/inventory/kpis");
      setKpis(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadSnaps = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (filters.status) params.set("status", filters.status);
      if (filters.housekeeping) params.set("housekeeping", filters.housekeeping);
      if (filters.building_id) params.set("building_id", filters.building_id);
      if (filters.floor_id) params.set("floor_id", filters.floor_id);
      if (filters.room_type_id) params.set("room_type_id", filters.room_type_id);
      if (filters.q) params.set("q", filters.q);
      if (filters.is_vip) params.set("is_vip", filters.is_vip === "true");
      const { data } = await api.get(`/inventory/snapshot?${params.toString()}`);
      setSnaps(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [page, pageSize, filters]);

  useEffect(() => {
    (async () => {
      const [b, f, r] = await Promise.all([
        loadLookup("buildings"), loadLookup("floors"), loadLookup("room-types"),
      ]);
      setBuildings(b); setFloors(f); setRoomTypes(r);
      const h = await loadLookup("hotel-profiles"); setHotels(h);
    })();
  }, []);

  useEffect(() => { loadKpis(); loadSnaps(); }, [loadKpis, loadSnaps]);

  // Poll lightly so dashboard refreshes after status changes from other tabs.
  useEffect(() => {
    const t = setInterval(() => { loadKpis(); }, 15000);
    return () => clearInterval(t);
  }, [loadKpis]);

  const refresh = () => { loadKpis(); loadSnaps(); };
  useStayCheckedOut(refresh);

  const setFilter = (k, v) => { setPage(1); setFilters({ ...filters, [k]: v }); };
  const clickKpi = (statusKey) => { if (statusKey === "total") setFilter("status", ""); else setFilter("status", statusKey); };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <PageHeader
        title="Room Inventory"
        subtitle="Operational snapshot — live"
        action={
          <Button variant="outline" onClick={refresh} data-testid="inventory-refresh">
            <RefreshCw className="w-4 h-4 mr-2"/>Refresh
          </Button>
        }
      />

      <div className="flex items-center gap-2" data-testid="inventory-mode-toggle">
        <button type="button" onClick={() => switchMode("grid")} data-testid="mode-grid" data-state={mode === "grid" ? "active" : "inactive"}
          className={`${MODE_BTN} ${mode === "grid" ? "bg-[#37F4B5] text-[#014751] border-[#37F4B5]" : "bg-white text-[#3F5257] border-[#D5E6E8] hover:bg-[#F4FCFC]"}`}><LayoutGrid className="w-3.5 h-3.5" strokeWidth={1.5} />Grid View</button>
        <button type="button" onClick={() => switchMode("timeline")} data-testid="mode-timeline" data-state={mode === "timeline" ? "active" : "inactive"}
          className={`${MODE_BTN} ${mode === "timeline" ? "bg-[#37F4B5] text-[#014751] border-[#37F4B5]" : "bg-white text-[#3F5257] border-[#D5E6E8] hover:bg-[#F4FCFC]"}`}><CalendarRange className="w-3.5 h-3.5" strokeWidth={1.5} />Timeline View</button>
      </div>

      {mode === "timeline" && <RoomTimeline floors={floors} roomTypes={roomTypes} onChanged={refresh} />}

      {mode === "grid" && <>
      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="inventory-kpis">
        {KPI_ORDER.map(([key, label]) => (
          <KpiCard
            key={key}
            label={label}
            value={kpis[key] ?? 0}
            tone={STATUS_TONE[key] || "neutral"}
            active={key !== "total" && filters.status === key}
            onClick={() => clickKpi(key)}
          />
        ))}
      </div>

      {/* Filters bar */}
      <Card className="p-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-hocp-steel"/>
            <Input
              placeholder="Search room / guest"
              aria-label="Search rooms by number or guest"
              value={filters.q}
              onChange={(e) => setFilter("q", e.target.value)}
              className="pl-9"
              data-testid="inventory-search"
            />
          </div>
          <Select value={filters.building_id || "all"} onValueChange={(v) => setFilter("building_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-building" aria-label="Filter by building"><SelectValue placeholder="Building"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Buildings</SelectItem>
              {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.floor_id || "all"} onValueChange={(v) => setFilter("floor_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-floor" aria-label="Filter by floor"><SelectValue placeholder="Floor"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Floors</SelectItem>
              {floors.map(f => <SelectItem key={f.id} value={f.id}>F{f.floor_number} {f.floor_name || ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.room_type_id || "all"} onValueChange={(v) => setFilter("room_type_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-roomtype" aria-label="Filter by room type"><SelectValue placeholder="Room Type"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {roomTypes.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.housekeeping || "all"} onValueChange={(v) => setFilter("housekeeping", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-hk" aria-label="Filter by housekeeping status"><SelectValue placeholder="Housekeeping"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any HK</SelectItem>
              <SelectItem value="clean">Clean</SelectItem>
              <SelectItem value="dirty">Dirty</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="inspection_pending">Inspection</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* View Switcher */}
      <div className="flex items-center justify-between">
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="view-switcher">
            <TabsTrigger value="card" data-testid="view-card"><LayoutGrid className="w-3.5 h-3.5 mr-1.5"/>Card</TabsTrigger>
            <TabsTrigger value="table" data-testid="view-table"><TableIcon className="w-3.5 h-3.5 mr-1.5"/>Table</TabsTrigger>
            <TabsTrigger value="floor" data-testid="view-floor"><Building2 className="w-3.5 h-3.5 mr-1.5"/>Floor</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="text-[12px] mono text-hocp-textSecondary">
          Showing <span className="text-hocp-textPrimary font-semibold">{snaps.length}</span> of <span className="text-hocp-textPrimary font-semibold">{total}</span>
          {loading && <span className="ml-2 text-hocp-textTertiary">loading…</span>}
        </div>
      </div>

      {/* Body */}
      {view === "card" && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="cards-grid">
          {snaps.map(s => <RoomCard key={s.id} snap={s} selected={s.room_id === selectedRoomId} onClick={() => setSelectedRoomId(s.room_id)}/>)}
          {!snaps.length && !loading && (
            <div className="col-span-full text-center text-hocp-textTertiary text-sm py-10">No rooms match the filters</div>
          )}
        </div>
      )}
      {view === "table" && <TableView snaps={snaps} onSelect={(s) => setSelectedRoomId(s.room_id)}/>}
      {view === "floor" && <FloorView snaps={snaps} floorsById={floorsById} selectedId={selectedRoomId} onSelect={(s) => setSelectedRoomId(s.room_id)}/>}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-hocp-textSecondary" data-testid="status-legend">
        {[["#14C48E", "Vacant / Clean"], ["#DC2626", "Occupied"], ["#014751", "Reserved"], ["#F59E0B", "Vacant / Dirty · Maintenance"], ["#97A6A9", "Blocked · Out of Order"]].map(([c, l]) => (
          <span key={l} className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{l}</span>
        ))}
      </div>

      {/* Pagination (only relevant for card/table; floor view loads same paged set) */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between text-[12px] mono text-hocp-textSecondary">
          <div>Page <span className="text-hocp-textPrimary font-semibold">{page}</span> of <span className="text-hocp-textPrimary font-semibold">{pageCount}</span></div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev"><ChevronLeft className="w-3.5 h-3.5"/></Button>
            <Button variant="outline" size="sm" disabled={page === pageCount} onClick={() => setPage(p => p + 1)} data-testid="page-next"><ChevronRight className="w-3.5 h-3.5"/></Button>
          </div>
        </div>
      )}

      <RoomActionDrawer
        roomId={selectedRoomId}
        onClose={() => setSelectedRoomId(null)}
        onChanged={refresh}
      />
      </>}
    </div>
  );
}
